// License Validations Page — full validation-event history for one license.
// Source of truth: GET /api/v1/licenses/:id/validations (validation_logs rows
// mapped to { id, timestamp, ip_address, success, error_code, response_time_ms }).
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router';
import { apiRequest } from '@/lib/supabase';
import { formatRelativeTime } from '@/lib/utils';
import { Breadcrumb } from '@/components/shared/Breadcrumb';
import { EmptyState } from '@/components/shared/EmptyState';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ArrowLeft, AlertTriangle } from 'lucide-react';

interface ValidationEvent {
  id: string;
  timestamp: string;
  ip_address: string;
  success: boolean;
  error_code: string | null;
  response_time_ms: number;
}

export function LicenseValidationsPage() {
  const { id } = useParams<{ id: string }>();
  const [events, setEvents] = useState<ValidationEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // Pull a generous window — this is the "view all" destination, not the
        // 10-row preview on the detail page. Backend caps limit at 100.
        const data = await apiRequest<ValidationEvent[]>(`/licenses/${id}/validations?limit=100`);
        setEvents(data || []);
        setLoadError(null);
      } catch (err) {
        setEvents([]);
        setLoadError(err instanceof Error ? err.message : 'Failed to load validations');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Link to={`/licenses/${id}`} style={{ color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }} aria-label="Back to license">
            <ArrowLeft size={18} />
          </Link>
          <div>
            <h1 className="page-title">Validations</h1>
            <p className="page-subtitle" style={{ fontFamily: 'monospace' }}>{id}</p>
          </div>
        </div>
      </div>

      <Breadcrumb
        crumbs={[
          { label: 'Licenses', to: '/licenses' },
          { label: id || '', to: `/licenses/${id}` },
          { label: 'Validations' },
        ]}
      />

      {loadError && (
        <div style={{ padding: '10px 12px', background: '#FEE2E2', borderRadius: '6px', marginBottom: '16px', fontSize: '13px', color: '#DC2626', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertTriangle size={14} /> {loadError}
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
            Loading validations...
          </div>
        ) : events.length === 0 ? (
          <EmptyState
            icon="shield-check"
            title="No validation events"
            body={loadError ? 'Validation history could not be loaded for this license.' : 'This license has not phoned home yet. Validation events will appear here once an install begins checking in.'}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>IP Address</TableHead>
                <TableHead>Result</TableHead>
                <TableHead>Error Code</TableHead>
                <TableHead style={{ textAlign: 'right' }}>Response Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map(v => (
                <TableRow key={v.id}>
                  <TableCell title={new Date(v.timestamp).toLocaleString()} style={{ color: 'var(--text-secondary)' }}>
                    {formatRelativeTime(v.timestamp)}
                  </TableCell>
                  <TableCell style={{ fontFamily: 'monospace' }}>{v.ip_address}</TableCell>
                  <TableCell>
                    {v.success
                      ? <span style={{ color: '#16A34A', fontWeight: 600 }}>✓ Valid</span>
                      : <span style={{ color: '#DC2626', fontWeight: 600 }}>✗ Failed</span>}
                  </TableCell>
                  <TableCell style={{ color: 'var(--text-muted)' }}>
                    {v.error_code || '—'}
                  </TableCell>
                  <TableCell style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                    {v.response_time_ms}ms
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

export default LicenseValidationsPage;
